import {type VariantProps} from 'class-variance-authority';
import * as React from 'react';

import {cn} from '@/utils/ui';

import {
  tableCellVariants,
  tableHeadVariants,
  tableVariants,
} from './table-variants';

type TableVariant = VariantProps<typeof tableVariants>['variant'];

const TableContext = React.createContext<TableVariant>('default');

const Table = React.forwardRef<
  HTMLTableElement,
  React.HTMLAttributes<HTMLTableElement> & {variant?: TableVariant}
>(({className, variant, ...props}, ref) => (
  <TableContext.Provider value={variant}>
    <table
      ref={ref}
      className={cn(tableVariants({variant}), className)}
      {...props}
    />
  </TableContext.Provider>
));
Table.displayName = 'Table';

const TableHeader = React.forwardRef<
  HTMLTableSectionElement,
  React.HTMLAttributes<HTMLTableSectionElement>
>(({className, ...props}, ref) => (
  <thead ref={ref} className={cn(className)} {...props} />
));
TableHeader.displayName = 'TableHeader';

const TableBody = React.forwardRef<
  HTMLTableSectionElement,
  React.HTMLAttributes<HTMLTableSectionElement>
>(({className, ...props}, ref) => (
  <tbody ref={ref} className={cn(className)} {...props} />
));
TableBody.displayName = 'TableBody';

const TableRow = React.forwardRef<
  HTMLTableRowElement,
  React.HTMLAttributes<HTMLTableRowElement>
>(({className, ...props}, ref) => (
  <tr ref={ref} className={cn(className)} {...props} />
));
TableRow.displayName = 'TableRow';

const TableHead = React.forwardRef<
  HTMLTableCellElement,
  React.ThHTMLAttributes<HTMLTableCellElement>
>(({className, ...props}, ref) => {
  const variant = React.useContext(TableContext);

  return (
    <th
      ref={ref}
      className={cn(tableHeadVariants({variant}), className)}
      {...props}
    />
  );
});
TableHead.displayName = 'TableHead';

const TableCell = React.forwardRef<
  HTMLTableCellElement,
  React.TdHTMLAttributes<HTMLTableCellElement>
>(({className, ...props}, ref) => {
  const variant = React.useContext(TableContext);

  return (
    <td
      ref={ref}
      className={cn(tableCellVariants({variant}), className)}
      {...props}
    />
  );
});
TableCell.displayName = 'TableCell';

export {Table, TableBody, TableCell, TableHead, TableHeader, TableRow};
